import React, { useState, useEffect, useCallback, useMemo } from 'react'; // ДОБАВЛЕН useMemo
import ModuleTemplate from '../../ModuleTemplate';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './VisionTest.css';

const S3_COLORS_URL = 'https://4da21c74-3916-49d9-84a3-03b7f1220048.selstorage.ru';
const S3_SHAPES_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/shapes';

const COLOR_IMAGES = {
  'красный': `${S3_COLORS_URL}/red.png`,
  'синий': `${S3_COLORS_URL}/blue.png`,
  'зеленый': `${S3_COLORS_URL}/green.png`,
  'желтый': `${S3_COLORS_URL}/yellow.png`,
  'розовый': `${S3_COLORS_URL}/pink.png`,
  'оранжевый': `${S3_COLORS_URL}/orange.png`,
  'голубой': `${S3_COLORS_URL}/lightblue.png`,
  'коричневый': `${S3_COLORS_URL}/brown.png`,
  'черный': `${S3_COLORS_URL}/black.png`,
  'белый': `${S3_COLORS_URL}/white.png`,
  'серый': `${S3_COLORS_URL}/grey.png`,
  'фиолетовый': `${S3_COLORS_URL}/purple.png`,
};

const SHAPE_IMAGES = {
  'круг': `${S3_SHAPES_URL}/circle.svg`,
  'овал': `${S3_SHAPES_URL}/oval.svg`,
  'треугольник': `${S3_SHAPES_URL}/triangle.svg`,
  'квадрат': `${S3_SHAPES_URL}/square.svg`,
  'прямоугольник': `${S3_SHAPES_URL}/rectangle.svg`,
  'многоугольник': `${S3_SHAPES_URL}/polygon.svg`,
  'шар': `${S3_SHAPES_URL}/sphere.svg`,
  'куб': `${S3_SHAPES_URL}/cube.svg`,
  'цилиндр': `${S3_SHAPES_URL}/cylinder.svg`,
};

// Module-level so it's referentially stable across renders — previously
// this was recreated inline in the component body on every render, which
// cascaded through ageData -> allQuestions (useMemo) -> currentQuestion ->
// speakCurrentQuestion (useCallback) -> the announce-effect, making every
// one of them "change" on every re-render and re-triggering the voiceover
// far more often than just on question change (heard twice or more).
const DATA_BY_AGE = {
  '4': {
    colors: ['красный', 'желтый', 'синий', 'зеленый', 'белый', 'черный'],
    shapes: ['круг', 'квадрат', 'овал', 'треугольник', 'шар', 'куб'],
  },
  '5': {
    colors: ['красный', 'оранжевый', 'желтый', 'зеленый', 'голубой', 'синий', 'белый', 'розовый', 'черный'],
    shapes: ['круг', 'квадрат', 'овал', 'треугольник', 'прямоугольник', 'шар', 'куб'],
  },
  '6': {
    colors: ['красный', 'оранжевый', 'желтый', 'зеленый', 'голубой', 'синий', 'фиолетовый', 'розовый', 'коричневый', 'серый', 'белый', 'черный'],
    shapes: ['круг', 'квадрат', 'овал', 'треугольник', 'прямоугольник', 'многоугольник', 'шар', 'куб', 'цилиндр'],
  },
};

const VisionTest = ({ ageGroup, onBack, onComplete }) => {
  const { play: playInstruction, stop: stopInstruction } = useInstructionAudio();
  const [currentPage, setCurrentPage] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [answerLog, setAnswerLog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const ageData = DATA_BY_AGE[ageGroup] || DATA_BY_AGE['4'];

  const getColorOptions = (correct, allColors) => {
    const others = allColors.filter(c => c !== correct);
    const shuffled = others.sort(() => Math.random() - 0.5).slice(0, 3);
    return [correct, ...shuffled].sort(() => Math.random() - 0.5);
  };

  const getShapeOptions = (correct, allShapes) => {
    const excludeMap = {
      'шар': ['круг'],
      'круг': ['шар'],
      'куб': ['квадрат', 'прямоугольник'],
      'квадрат': ['куб'],
      'прямоугольник': ['куб'],
      'цилиндр': ['шар', 'круг'],
    };

    const exclude = excludeMap[correct] || [];
    const others = allShapes.filter(s => s !== correct && !exclude.includes(s));
    const shuffled = others.sort(() => Math.random() - 0.5).slice(0, 3);
    return [correct, ...shuffled].sort(() => Math.random() - 0.5);
  };

  // ИЗМЕНЕНИЕ 1: Обернули в useMemo, чтобы вопросы не пересоздавались при каждом рендере
  const allQuestions = useMemo(() => {
    const colorQuestions = ageData.colors.map(color => ({
      id: `color-${color}`,
      type: 'color',
      prompt: `Найди ${color}`,
      correct: color,
      options: getColorOptions(color, ageData.colors),
    }));

    const shapeQuestions = ageData.shapes.map(shape => ({
      id: `shape-${shape}`,
      type: 'shape',
      prompt: `Покажи ${shape}`,
      correct: shape,
      options: getShapeOptions(shape, ageData.shapes),
    }));

    return [...colorQuestions, ...shapeQuestions];
  }, [ageData]);

  const totalQuestions = allQuestions.length;
  const currentQuestion = allQuestions[currentPage];
  const isLastQuestion = currentPage === allQuestions.length - 1;

    // Функция озвучки текущего вопроса
  const speakCurrentQuestion = useCallback(() => {
    if (!currentQuestion) return;
    playInstruction(currentQuestion.prompt);
  }, [currentQuestion]);

  // Автоматическая озвучка при смене вопроса
  useEffect(() => {
    speakCurrentQuestion();
  }, [currentPage, speakCurrentQuestion]);

  // Кнопка «Повторить инструкцию»
  const repeatInstruction = useCallback(() => {
    speakCurrentQuestion();
  }, [speakCurrentQuestion]);

  const handleSelect = (option) => {
    if (selected !== null) return;

    stopInstruction(); // Останавливаем озвучку при ответе
    setSelected(option);
    const isCorrect = option === currentQuestion.correct;
    setAnswers([...answers, isCorrect]);
    setAnswerLog(prev => [...prev, { category: currentQuestion.type, value: currentQuestion.correct, correct: isCorrect }]);

    setTimeout(() => {
      if (isLastQuestion) {
        setShowResult(true);
      } else {
        setCurrentPage(currentPage + 1);
        setSelected(null);
      }
    }, 800);
  };

  const renderQuestionContent = () => {
    if (!currentQuestion) return null;
    const options = currentQuestion.options;
    const isColor = currentQuestion.type === 'color';

    return (
      <div className="vision-images-grid">
        {options.map((opt) => (
          <button
            key={opt}
            className={`vision-image-btn ${selected === opt ? 'selected' : ''}`}
            onClick={() => handleSelect(opt)}
          >
            <img
              src={isColor ? COLOR_IMAGES[opt] : SHAPE_IMAGES[opt]}
              alt={opt}
              className="vision-image"
              onError={(e) => {
                e.target.style.display = 'none';
                if (isColor) e.target.parentElement.style.backgroundColor = opt;
              }}
            />
          </button>
        ))}
      </div>
    );
  };

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;

        const categoryTotals = {};
        answerLog.forEach(({ category, correct }) => {
          if (!categoryTotals[category]) categoryTotals[category] = { total: 0, correct: 0 };
          categoryTotals[category].total += 1;
          if (correct) categoryTotals[category].correct += 1;
        });
        const byCategory = {};
        Object.entries(categoryTotals).forEach(([cat, { total, correct }]) => {
          byCategory[cat] = Math.round((correct / total) * 100);
        });

        const colorLog = answerLog.filter(a => a.category === 'color');
        const shapeLog = answerLog.filter(a => a.category === 'shape');
        const correctColors = [...new Set(colorLog.filter(a => a.correct).map(a => a.value))];
        const failedColors = [...new Set(colorLog.filter(a => !a.correct).map(a => a.value))];
        const correctShapes = [...new Set(shapeLog.filter(a => a.correct).map(a => a.value))];
        const failedShapes = [...new Set(shapeLog.filter(a => !a.correct).map(a => a.value))];

        if (onComplete) {
          onComplete({
            testId: 'VisionTest',
            score,
            details: {
              total: totalQuestions,
              correct: correctCount,
              byCategory,
              correctColors,
              failedColors,
              correctShapes,
              failedShapes,
            },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate
        progress={answers}
        totalQuestions={totalQuestions}
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="result-content">
          <h2 className="result-title">🎉 Модуль завершён!</h2>
          <div className="result-score">
            <span className="score-number">{correctCount}</span>
            <span className="score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) {
    return (
      <ModuleTemplate
        progress={answers}
        totalQuestions={1}
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="loading-text">Загрузка...</div>
      </ModuleTemplate>
    );
  }

  return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={totalQuestions}
      forceStop={true}
      onComplete={onComplete || onBack}
      onRepeat={repeatInstruction}
      extraContent={currentQuestion.prompt}
      disabled={selected !== null}
    >
       <div className="vision-container">
          {renderQuestionContent()}
      </div>
    </ModuleTemplate>
  );
};

export default VisionTest;